/**
 * React Query mutations for stories
 * Story 198: View and filter stories in backlog
 * Story 274: QA workflow status transitions
 * Story 356: Web event instrumentation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { storyKeys } from './useStories';
import { storyDetailKeys } from './useStory';
import { track } from '../lib/analytics';

/** Parameters for updating a story title */
interface UpdateStoryTitleParams {
  projectSlug: string;
  storyExternalId: string;
  title: string;
}

/** Parameters for reordering a story */
interface ReorderStoryParams {
  projectSlug: string;
  storyExternalId: string;
  targetEpic: string | null;
  targetPriority: number;
}

/** Parameters for deleting a story */
interface DeleteStoryParams {
  projectSlug: string;
  storyExternalId: string;
}

/** Parameters for creating a story */
interface CreateStoryParams {
  projectSlug: string;
  title: string;
  afterStoryId?: string; // Insert after this story (external_id)
}

/** Parameters for updating a story's status (Story 274, 284) */
interface UpdateStoryStatusParams {
  projectSlug: string;
  storyExternalId: string;
  status: string;
  qa_evidence?: Record<string, string>;  // Required for qa → done transition
  human_override?: boolean;  // Story 284: Override for qa → done without evidence
}

/**
 * Mutation to update a story's title
 */
export function useUpdateStoryTitle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectSlug, storyExternalId, title }: UpdateStoryTitleParams) => {
      return api.fetch(`/api/v1/stories/${storyExternalId}`, {
        method: 'PATCH',
        body: JSON.stringify({ projectSlug, title }),
      });
    },
    onSuccess: (_, { projectSlug, storyExternalId }) => {
      // Story 356: Track story update
      track({ name: 'story_updated', properties: { story_id: storyExternalId, fields_changed: ['title'] } });

      // Invalidate stories list and individual story detail
      queryClient.invalidateQueries({ queryKey: storyKeys.list(projectSlug) });
      queryClient.invalidateQueries({ queryKey: storyDetailKeys.detail(projectSlug, storyExternalId) });
    },
  });
}

/**
 * Mutation to reorder a story (change epic or priority)
 */
export function useReorderStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectSlug,
      storyExternalId,
      targetEpic,
      targetPriority,
    }: ReorderStoryParams) => {
      return api.fetch(`/api/v1/stories/${storyExternalId}/reorder`, {
        method: 'POST',
        body: JSON.stringify({ projectSlug, targetEpic, targetPriority }),
      });
    },
    onSuccess: (_, { projectSlug, storyExternalId, targetEpic, targetPriority }) => {
      // Story 356: Track story reorder
      track({
        name: 'story_reordered',
        properties: {
          story_id: storyExternalId,
          target_epic: targetEpic ?? undefined,
          priority: targetPriority,
        },
      });

      // Invalidate stories list to refetch
      queryClient.invalidateQueries({ queryKey: storyKeys.list(projectSlug) });
    },
  });
}

/**
 * Mutation to soft-delete a story (marks as deleted, can be restored)
 */
export function useDeleteStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectSlug, storyExternalId }: DeleteStoryParams) => {
      return api.fetch(`/api/v1/stories/${storyExternalId}`, {
        method: 'DELETE',
        body: JSON.stringify({ projectSlug }),
      });
    },
    onSuccess: (_, { projectSlug, storyExternalId }) => {
      // Story 356: Track story deletion
      track({ name: 'story_deleted', properties: { story_id: storyExternalId } });

      queryClient.invalidateQueries({ queryKey: storyKeys.list(projectSlug) });
    },
  });
}

/**
 * Mutation to create a new story
 * Story 227: Edit PRDs, tech designs and tasks
 */
export function useCreateStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectSlug, title, afterStoryId }: CreateStoryParams) => {
      return api.fetch(
        `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories`,
        {
          method: 'POST',
          body: JSON.stringify({ title, afterStoryId }),
        }
      );
    },
    onSuccess: (data, { projectSlug, title }) => {
      // Story 356: Track story creation
      // API returns { external_id: string, ... }
      const storyId = (data as { external_id?: string })?.external_id || 'unknown'; // FALLBACK: Analytics non-blocking - track with placeholder if malformed
      track({ name: 'story_created', properties: { story_id: storyId, title } });

      queryClient.invalidateQueries({ queryKey: storyKeys.list(projectSlug) });
    },
  });
}

/**
 * Mutation to update a story's status (Story 274: QA workflow)
 *
 * Handles status transitions with QA workflow rules:
 * - in-progress → qa (requires gates to pass)
 * - qa → done (requires qa_evidence)
 * - qa → in-progress (rework path)
 */
export function useUpdateStoryStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectSlug,
      storyExternalId,
      status,
      qa_evidence,
      human_override,
    }: UpdateStoryStatusParams) => {
      return api.fetch(
        `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyExternalId}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status, qa_evidence, human_override }),
        }
      );
    },
    onSuccess: (data, { projectSlug, storyExternalId, status }) => {
      // Story 356: Track story status change
      // API returns { from_status: string, status: string, ... }
      const response = data as { from_status?: string; status?: string };
      track({
        name: 'story_status_changed',
        properties: {
          story_id: storyExternalId,
          from_status: response.from_status || 'unknown', // FALLBACK: Analytics non-blocking - use placeholder if missing
          to_status: status,
        },
      });

      // Invalidate stories list and individual story detail
      queryClient.invalidateQueries({ queryKey: storyKeys.list(projectSlug) });
      queryClient.invalidateQueries({ queryKey: storyDetailKeys.detail(projectSlug, storyExternalId) });
    },
  });
}
